import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Select,
  Space,
  Switch,
  Tag,
  message,
} from 'antd';
import { captureAnalytics } from '../../../actions/preload';

const settingEnabled = (value) => value === true || value === 'true';

// antd 4 (pinned 4.17.4) has no `optionRender` — that is a v5 prop and silently
// does nothing here. A ReactNode `label` on the option is the v4 way to render a
// colour chip, and because `label` is then not a string, search needs its own
// plain-text field.
const filterByName = (input, option) => (
  (option?.name || '').toLowerCase().includes(input.toLowerCase())
);

// Labels are addressed by slug everywhere in this API (Label#object returns
// `id: slug`), categories by their numeric id. Keep the two lists in the shape
// the endpoint expects rather than normalising them into one.
const AutoArchiveOnLabelSettings = ({
  settings,
  updateSettings,
  requestedEnabled,
  labels,
  documentCategories,
}) => {
  const [enabled, setEnabled] = useState(false);
  const [labelIds, setLabelIds] = useState([]);
  const [categoryIds, setCategoryIds] = useState([]);
  const [saving, setSaving] = useState(false);
  // The draft stops following `settings` the moment the admin edits it or a save
  // completes. Opening the modal starts a settings GET while these controls are
  // already editable, so without this a slow response lands on top of selections
  // made while it was in flight, and a GET that resolves after a PUT restores the
  // values the PUT just replaced.
  const draftOwnedByUser = useRef(false);

  useEffect(() => {
    if (draftOwnedByUser.current) return;

    const nextSettings = settings || {};

    // requestedEnabled carries the table switch's requested value and has to win
    // in both directions: an explicit `false` is a request to turn the rule off,
    // so it must not fall back to the persisted `true`. Only null (the Configure
    // button, which requests no transition) defers to the saved setting.
    setEnabled(typeof requestedEnabled === 'boolean' ? requestedEnabled : settingEnabled(nextSettings.enabled));
    setLabelIds(nextSettings.label_ids || []);
    setCategoryIds(nextSettings.document_category_ids || []);
  }, [settings, requestedEnabled]);

  const nothingSelected = labelIds.length === 0 && categoryIds.length === 0;
  const blockedByEmptySelection = enabled && nothingSelected;

  const labelOptions = useMemo(() => (
    (labels || []).map((label) => ({
      value: label.id,
      name: label.name,
      label: <Tag color={label.color}>{label.name}</Tag>,
    }))
  ), [labels]);

  // Categories without any usable name are dropped, so build and filter in the
  // same pass rather than mapping, filtering, then mapping again.
  const categoryOptions = useMemo(() => (
    (documentCategories || []).reduce((options, category) => {
      const name = category.ai_category_name || category.emr_category || category.ai_tag;

      if (name) options.push({ value: category.id, name, label: name });

      return options;
    }, [])
  ), [documentCategories]);

  // Counts only, never label or category names: those are organization
  // configuration, and the product question ("do orgs adopt this, and how wide
  // do they cast the net?") is answered by counts. Success and failure emit the
  // same event so the failure count has a denominator.
  const trackSave = (succeeded) => captureAnalytics('auto_archive_on_label_settings_saved', {
    succeeded,
    enabled,
    label_count: labelIds.length,
    category_count: categoryIds.length,
  });

  const handleEnabledChange = (checked) => {
    draftOwnedByUser.current = true;
    setEnabled(checked);
    captureAnalytics('auto_archive_on_label_toggled', { enabled: checked });
  };

  const handleLabelsChange = (value) => {
    draftOwnedByUser.current = true;
    setLabelIds(value);
    captureAnalytics('auto_archive_on_label_labels_changed', { label_count: value.length });
  };

  const handleCategoriesChange = (value) => {
    draftOwnedByUser.current = true;
    setCategoryIds(value);
    captureAnalytics('auto_archive_on_label_categories_changed', { category_count: value.length });
  };

  const handleSave = async () => {
    if (blockedByEmptySelection) return;

    setSaving(true);

    try {
      await updateSettings({
        enabled,
        label_ids: labelIds,
        document_category_ids: categoryIds,
      });
      trackSave(true);
      draftOwnedByUser.current = true;
      message.success('Settings saved successfully');
    } catch (error) {
      trackSave(false);
      const errorMsg = error?.response?.data?.error || 'Failed to save settings';
      message.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      <Space>
        <Switch checked={enabled} onChange={handleEnabledChange} />
        <span>Archive matching documents automatically</span>
      </Space>

      {enabled && (
        <Alert
          type="warning"
          showIcon
          message="Matching documents are archived as soon as the label or category is applied."
          description="They stay searchable under Archived and can be restored. A document someone is assigned to is left alone."
        />
      )}

      <div>
        <div style={{ marginBottom: 4 }}>Labels</div>
        <Select
          mode="multiple"
          allowClear
          showSearch
          filterOption={filterByName}
          style={{ width: '100%' }}
          placeholder="Search labels"
          value={labelIds}
          onChange={handleLabelsChange}
          options={labelOptions}
        />
      </div>

      <div>
        <div style={{ marginBottom: 4 }}>Categories</div>
        <Select
          mode="multiple"
          allowClear
          showSearch
          filterOption={filterByName}
          style={{ width: '100%' }}
          placeholder="Search categories"
          value={categoryIds}
          onChange={handleCategoriesChange}
          options={categoryOptions}
        />
      </div>

      {blockedByEmptySelection && (
        <Alert type="error" message="Pick at least one label or category." />
      )}

      <Button
        type="primary"
        loading={saving}
        disabled={blockedByEmptySelection}
        onClick={handleSave}
      >
        Save
      </Button>
    </Space>
  );
};

AutoArchiveOnLabelSettings.propTypes = {
  settings: PropTypes.object,
  updateSettings: PropTypes.func.isRequired,
  requestedEnabled: PropTypes.bool,
  labels: PropTypes.array,
  documentCategories: PropTypes.array,
};

AutoArchiveOnLabelSettings.defaultProps = {
  settings: null,
  requestedEnabled: null,
  labels: [],
  documentCategories: [],
};

export default AutoArchiveOnLabelSettings;
